import { Platform } from 'react-native'
import Constants from 'expo-constants'
import { Mixpanel } from 'mixpanel-react-native'

import type { MixpanelEventName } from './event-names'
import { LegacyEventNameMap } from './event-names'

let mixpanelInstance: Mixpanel | null = null
let sessionProperties: Record<string, unknown> = {}

const DUAL_FIRE_LEGACY_EVENTS = true
const MIXPANEL_EU_SERVER_URL = 'https://api-eu.mixpanel.com'

function getMixpanelToken(): string {
  const config = Constants.expoConfig?.extra?.mixpanel as
    | { token?: string }
    | undefined
  return config?.token ?? ''
}

export async function initMixpanel(): Promise<void> {
  const token = getMixpanelToken()
  if (!token) {
    console.warn('Mixpanel token not configured, analytics disabled')
    return
  }

  try {
    const trackAutomaticEvents = false
    if (Platform.OS === 'web') {
      mixpanelInstance = new Mixpanel(token, trackAutomaticEvents, false)
    } else {
      mixpanelInstance = new Mixpanel(token, trackAutomaticEvents, true)
    }
    await mixpanelInstance.init(
      false, // optOutTrackingDefault
      {}, // superProperties
      MIXPANEL_EU_SERVER_URL,
    )
  } catch (e) {
    console.warn('Failed to initialize Mixpanel', e)
    mixpanelInstance = null
  }
}

export function trackEvent(
  event: MixpanelEventName,
  properties?: Record<string, unknown>,
): void {
  if (mixpanelInstance == null) return

  const merged: Record<string, unknown> = {
    ...sessionProperties,
    ...properties,
  }

  const hasProps = Object.keys(merged).length > 0

  if (hasProps) {
    mixpanelInstance.track(event, merged)
  } else {
    mixpanelInstance.track(event)
  }

  if (DUAL_FIRE_LEGACY_EVENTS) {
    const legacyName = LegacyEventNameMap[event]
    if (legacyName != null) {
      if (hasProps) {
        mixpanelInstance.track(legacyName, merged)
      } else {
        mixpanelInstance.track(legacyName)
      }
    }
  }
}

export async function identifyUser(
  userId: string,
  name?: string | null,
  email?: string | null,
  extras?: {
    platform?: string
    appVersion?: string
  },
): Promise<void> {
  if (mixpanelInstance == null) return

  await mixpanelInstance.identify(userId)
  const people = mixpanelInstance.getPeople()
  if (name != null) people.set('$name', name)
  if (email != null) people.set('$email', email)
  people.set('platform', extras?.platform ?? Platform.OS)
  people.set('last_login', new Date().toISOString())
  if (extras?.appVersion != null) {
    people.set('app_version', extras.appVersion)
  }
}

export function resetAnalytics(): void {
  if (mixpanelInstance == null) return

  mixpanelInstance.reset()
  clearSessionProperties()
}

export function registerSuperProperties(
  properties: Record<string, unknown>,
): void {
  if (mixpanelInstance == null) return
  mixpanelInstance.registerSuperProperties(properties)
}

export function setSessionProperties(
  properties: Record<string, unknown>,
): void {
  sessionProperties = { ...sessionProperties, ...properties }
}

export function clearSessionProperties(): void {
  sessionProperties = {}
}

export function isMixpanelInitialized(): boolean {
  return mixpanelInstance != null
}
